import { cuisineOptions, priceRangeOptions, dietaryOptions, atmosphereOptions, musicOptions, languageOptions, interestOptions } from '../data/filterOptions';

// Generic filter function that can be used across different pages
export const filterItems = <T>(
  items: T[],
  searchQuery: string,
  selectedFilters: string[],
  getSearchableText: (item: T) => string,
  getFilterableProperties: (item: T) => Record<string, any>
): T[] => {
  // First apply search query if it exists
  let filteredItems = items;
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    filteredItems = filteredItems.filter(item => {
      // Get only the name for searching
      const searchableText = getSearchableName(item).toLowerCase();
      return searchableText.includes(query);
    });
  }

  // Then apply filters if any are selected
  if (selectedFilters.length > 0) {
    filteredItems = filteredItems.filter(item => {
      const properties = getFilterableProperties(item);
      
      // Check if any of the selected filters match the item's properties
      return selectedFilters.some(filter => {
        // Special case: "?" price range means "any" price range
        if (filter === "?") {
          return true; // Always match for "any" price range
        }
        
        // Check if the filter is a price range option
        const priceOption = priceRangeOptions.find(option => 
          typeof option === 'object' && option.value === filter
        );
        
        if (priceOption && properties.price_range) {
          return properties.price_range === filter;
        }
        
        // Check if the filter is in cuisine options and matches the item's cuisine type
        if (cuisineOptions.includes(filter) && properties.cuisine_type) {
          return properties.cuisine_type.toLowerCase() === filter.toLowerCase();
        }
        
        // Check if the filter is in dietary options and matches any of the item's dietary options
        if (dietaryOptions.includes(filter) && Array.isArray(properties.dietary_options)) {
          return properties.dietary_options.some((option: string) => 
            option.toLowerCase() === filter.toLowerCase()
          );
        }
        
        // Check if the filter is in atmosphere options and matches the item's atmosphere
        if (atmosphereOptions.includes(filter) && properties.bar_type) {
          return properties.bar_type.toLowerCase() === filter.toLowerCase();
        }
        
        // Check if the filter is in music options and matches the item's music
        if (musicOptions.includes(filter) && properties.features) {
          return properties.features.live_music === true;
        }
        
        // Check if the filter is in language options and matches any of the item's languages
        if (languageOptions.includes(filter) && Array.isArray(properties.languages)) {
          return properties.languages.some((language: string) => 
            language.toLowerCase() === filter.toLowerCase()
          );
        }
        
        // Check if the filter is in interest options and matches any of the item's interests
        if (interestOptions.includes(filter) && Array.isArray(properties.interests)) {
          return properties.interests.some((interest: string) => 
            interest.toLowerCase() === filter.toLowerCase()
          );
        }
        
        return false;
      });
    });
  }

  return filteredItems;
};

// Helper function to extract only the name for searching
export const getSearchableName = (item: any): string => {
  // For venues (restaurants or bars)
  if (item.name) {
    return item.name;
  }
  
  // For events (dinners, blind dates, drinking nights)
  if (item.restaurant || item.bar) {
    const venue = item.restaurant || item.bar;
    return venue?.name || '';
  }
  
  // For profiles
  if (item.full_name) {
    return item.full_name;
  }
  
  return '';
};

// Helper function to extract searchable text from a restaurant or bar
export const getVenueSearchableText = (venue: any): string => {
  return [
    venue.name,
    venue.cuisine_type,
    venue.bar_type,
    venue.address,
    venue.description
  ].filter(Boolean).join(' ');
};

// Helper function to extract searchable text from a dinner or event
export const getEventSearchableText = (event: any): string => {
  const venue = event.restaurant || event.bar;
  return [
    venue?.name,
    venue?.cuisine_type,
    venue?.bar_type,
    venue?.address,
    event.description,
    event.dining_style,
    event.price_range
  ].filter(Boolean).join(' ');
};

// Helper function to extract searchable text from a profile
export const getProfileSearchableText = (profile: any): string => {
  return [
    profile.full_name,
    profile.location,
    profile.bio,
    ...(profile.languages || []),
    ...(profile.interests || []),
    ...(profile.favorite_cuisines || []),
    profile.dining_style
  ].filter(Boolean).join(' ');
};

// Helper function to get filterable properties from a venue
export const getVenueFilterableProperties = (venue: any): Record<string, any> => {
  return {
    price_range: venue.price_range,
    cuisine_type: venue.cuisine_type,
    bar_type: venue.bar_type,
    style: venue.style,
    dietary_options: venue.dietary_options,
    features: venue.features
  };
};

// Helper function to get filterable properties from an event
export const getEventFilterableProperties = (event: any): Record<string, any> => {
  const venue = event.restaurant || event.bar;
  return {
    price_range: event.price_range,
    cuisine_type: venue?.cuisine_type,
    bar_type: venue?.bar_type,
    dietary_options: venue?.dietary_options,
    features: venue?.features,
    dining_style: event.dining_style
  };
};

// Helper function to get filterable properties from a profile
export const getProfileFilterableProperties = (profile: any): Record<string, any> => {
  return {
    languages: profile.languages,
    interests: profile.interests,
    location: profile.location,
    dietary_preferences: profile.dietary_preferences,
    dining_style: profile.dining_style,
    style: profile.style,
    favorite_cuisines: profile.favorite_cuisines,
    drinking_preference: profile.drinking_preference,
    smoking_preference: profile.smoking_preference
  };
};